import { IMessenger, createClient } from "@ms/utilities-cross-window";
import { IReadonlyObservableValue, ObservableArray } from "azure-devops-ui/Core/Observable";
import React from "react";
import { unstable_batchedUpdates } from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { Scenario } from "./common/components/scenario/scenario";
import { EventContext } from "./common/contexts/event";
import { FeatureContext } from "./common/contexts/feature";
import { LocaleStorageSettingsContext, SettingsContext } from "./common/contexts/settings";
import { ThemeContext } from "./common/contexts/theme";
import { useFetch } from "./common/hooks/usefetch";
import { useObjectURL } from "./common/hooks/useobjecturl";
import { useObservable, useSubscription, useSubscriptionArray } from "./common/hooks/useobservable";
import { useTimeout } from "./common/hooks/usetimeout";
import { getCookie, setCookie } from "./common/utilities/browser";
import { IFetchCompleteEvent, IFetchPrepareEvent } from "./common/utilities/fetch";
import { format } from "./common/utilities/format";
import { noop } from "./common/utilities/func";
import { ITelemetryEvent } from "./common/utilities/platformdispatch";
import { getAccount } from "./photos/api/account";
import { sendFeedback } from "./photos/api/feedback";
import { IGraphError } from "./photos/api/network";
import { getProfile, getProfileImage } from "./photos/api/profile";
import { getUserEmailPreferences } from "./photos/api/useremailpreferences";
import { getUserPreferences } from "./photos/api/userpreferences";
import { apiOriginCOB, apiVersionCOB, getApiConfiguration, setApiConfiguration } from "./photos/api/util";
import { AuthorizationContext, PhotoAuthorizationContext } from "./photos/contexts/authorization";
import { BackgroundContext } from "./photos/contexts/background";
import { EmbedContext, IEmbedContext } from "./photos/contexts/embed";
import { PhotoFeatureContext } from "./photos/contexts/feature";
import { FeedbackContext, SendFeedbackContext } from "./photos/contexts/feedback";
import { MetaNavigation } from "./photos/contexts/metanavigation";
import { PhotoSearchContext, SearchContext } from "./photos/contexts/search";
import { SessionContext, getSignInUri } from "./photos/contexts/session";
import { PhotoThemeContext } from "./photos/contexts/theme";
import { PhotoUserPreferencesContext, UserPreferencesContext } from "./photos/contexts/userpreferences";
import { WorkerContext } from "./photos/contexts/worker";
import { useOneDS } from "./photos/hooks/useoneds";
import { useRejectionNoOp } from "./photos/hooks/userejection";
import { IAccount } from "./photos/types/account";
import { UXSetting } from "./photos/types/settings";
import { IWorkerDetails, IWorkerMessage } from "./photos/types/worker";
import { IAuthenticateCommand, IEmbedOptions } from "./photos/types/xdm";
import { filterUrl } from "./photos/utilities/util";
import { Shell } from "./shell";

import "./styles.ts";

/**
 * The endTimeout variable determines the length of time a session is considered inactive
 * before being considered ended.
 * @constant {number}
 * @default 15mins
 */
const endTimeout = 1000 * 60 * 15;

export interface IApplicationProps {
  workerDetails: IReadonlyObservableValue<IWorkerDetails>;
}

export function Application(props: IApplicationProps): React.ReactElement | null {
  const { workerDetails } = props;

  const backgroundContext = React.useContext(BackgroundContext);

  // We will use the event context throughout the application to communicate the
  // details af events that are taking place, allowing other code to react to them.
  const eventContext = React.useContext(EventContext);

  // Get the details about the current request session.
  const sessionContext = React.useContext(SessionContext);

  // Start with the default settings context, if the user is logged in we will
  // create a context specific to the user.
  let settingsContext = React.useContext(SettingsContext);

  // Track how long user engagemed in this session
  const startTime = React.useRef(Date.now());
  const lastInteractTime = React.useRef(startTime.current);
  const numUserActions = React.useRef(0);
  const settingsLoaded = React.useRef(false);

  // Setup an observable that notes when the application load scenario is complete.
  const [accountDetails, setAccountDetails] = useObservable<IAccount | undefined>(undefined);
  const [loadComplete, setLoadComplete] = useObservable(false);

  const { setTimeout } = useTimeout();

  const searchParams = new URLSearchParams(window.location.search);
  const [additionalParameters] = React.useState(new URLSearchParams());

  // Determine whether or not the current session is authenticated.
  const userAuthenticated = sessionContext.authenticated();

  // Before a network request starts add our required authentication headers.
  // When a network request completes we will record the results in telemetry
  eventContext.addEventListener("fetchPrepare", prepareRequest);
  eventContext.addEventListener("fetchComplete", detectMigratedUser);
  eventContext.addEventListener("fetchComplete", recordNetworkTelemetry);

  // If the user is authenticated we want to create a settings context that
  // will save the settings for the authenticated user.
  if (sessionContext.userId) {
    settingsContext = new LocaleStorageSettingsContext(`/${sessionContext.userId}/`);
  }

  // Create an observable array that will contain the recent searches.
  // We will use the set function to update the recent searches when needed.
  const recentSearches = new ObservableArray<string>([]);

  // When the array of searches changes we will update the setting on the service.
  useSubscriptionArray(recentSearches, () => {
    // Don't save until the setitngs have been loaded, and don't update when the
    // settings are loaded the first time.
    if (settingsLoaded.current) {
      userPreferencesContext
        .setUserPreferences("feature", {
          recentSearches: JSON.stringify(recentSearches.value)
        })
        .catch(noop);
    }
  });

  // Create our contexts from the photo feature implementation.
  const authorizationContext = new PhotoAuthorizationContext(eventContext, sessionContext, undefined, undefined, acquireHostToken);
  const featureContext = new PhotoFeatureContext(settingsContext, sessionContext);
  const searchContext = new PhotoSearchContext(recentSearches);
  const themeContext = new PhotoThemeContext(settingsContext);
  const userPreferencesContext = new PhotoUserPreferencesContext(eventContext, settingsContext);

  // Before we start processing the page generation, if we need to update the
  // local route we will do that now.
  if (sessionContext.pathname) {
    window.history.replaceState(null, "", sessionContext.pathname);
  }

  // @CONVERGENCE: Ensure the sessionContext's migrated state is consistent with the application configuration.
  const userState = settingsContext.getSetting<{ migrated?: boolean }>("userState");
  const [migrated, setMigrated] = React.useState(userState?.migrated);

  if (migrated) {
    sessionContext.migrated = true;
    setApiConfiguration(apiOriginCOB, apiVersionCOB);
  }

  // The initial context will use the session userId for the driveId if it is available.
  // If the current driveId is not available or is set to "me" use the userId.
  if (sessionContext.userId && (!sessionContext.driveId || sessionContext.driveId === "me")) {
    let driveId = sessionContext.userId;

    // @HACK: The driveId is not 0 prefixed but the userId, we will remove any prefix 0's
    if (!sessionContext.migrated) {
      while (driveId.charCodeAt(0) === 48) {
        driveId = driveId.substring(1);
      }
    }

    sessionContext.driveId = driveId;
  }

  // DEVELOPMENT: Show the telemetry in the console.
  if (process.env.NODE_ENV !== "test" && (sessionContext.environment === "development" || getCookie("Development"))) {
    eventContext.addEventListener("telemetryAvailable", consoleCallback);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
      return () => {
        eventContext.removeEventListener("telemetryAvailable", consoleCallback);
      };
    });

    function consoleCallback(event: ITelemetryEvent) {
      console.debug(event.action, event);
    }
  }

  // Setup the usage of our telemetry system. We need to pass the featureContext
  // since we have not mounted the custom featureContext provider.
  const oneDSCallback = useOneDS(featureContext);

  const telemetryCallback = (event: ITelemetryEvent) => {
    const filteredResult = filterResult(event);

    // Send the resulting event through the telemetry reporting systems.
    oneDSCallback && oneDSCallback(filteredResult);

    function filterResult(event: ITelemetryEvent): ITelemetryEvent {
      const filteredEvent: ITelemetryEvent = { ...event };

      // Ensure that when the event was successfully fulfilled we don't keep the
      // value, instead just record success. On a failure, we will record the
      // reason.
      if (event.result) {
        if (event.result.status === "fulfilled") {
          filteredEvent.result = { status: "fulfilled" };
        } else {
          filteredEvent.result = event.result;
        }
      }

      // We have some general requirements for filtering logging, some details can
      if (filteredEvent.url) {
        filteredEvent.url = filterUrl(new URL(filteredEvent.url)).toString();
      }

      if (filteredEvent.children) {
        // Filter any children events before returning.
        const children: ITelemetryEvent[] = [];
        for (const childEvent of filteredEvent.children) {
          children.push(filterResult(childEvent));
        }

        filteredEvent.children = children;
      }

      return filteredEvent;
    }
  };

  eventContext.addEventListener("telemetryAvailable", telemetryCallback);
  eventContext.addEventListener("telemetryAvailable", updateSessionMetrics);

  // Add an beforeUnload handler to process pending work before unloading the page.
  // @TODO: This is an anti-pattern and is causing lighthouse warnings, this needs to be addressed.
  window.addEventListener("beforeunload", beforUnload);

  // Add an unhandled exception handler to capture exceptions and convert them
  // into telemetry events.
  window.addEventListener("error", unhandledException);

  // Add a message handler that responds to service worker messages.
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", handleWorkerMessage);
  }

  // Process the embed options and apply any display options to the application.
  const embed = searchParams.get("embed");
  const { embedContext, scenarioComplete, scenarioStart } = React.useMemo(() => {
    let embedContext: IEmbedContext = {};

    if (embed && typeof embed === "string") {
      try {
        const embedOptions = JSON.parse(embed) as IEmbedOptions;
        let hostClient: (IMessenger & { dispose: () => void }) | undefined;

        // Ensure the embed parameters tracked across navigations.
        additionalParameters.set("embed", embed);

        // The application should have the embedded css class on the body, when
        // opened in embedded mode.
        document.body.classList.add("embedded");

        // Setup the defaults for the embedded options.
        embedOptions.theme = embedOptions.theme || "light";

        const { display, messaging, network } = embedOptions;

        if (display?.className && typeof display.className === "string") {
          document.body.classList.add(display.className);
        }

        // Update the theme with the supplied value, if one isnt supplied default to dark mode (SPO default)
        if (typeof embedOptions.theme === "string") {
          document.body.setAttribute("data-theme", embedOptions.theme);
        }

        // Setup our message channel if one is defined by the host.
        if (messaging) {
          const { channelId, origin } = messaging;

          if (channelId && origin && typeof channelId === "string" && typeof origin === "string") {
            hostClient = createClient({ channelId, origin });
          }
        }

        // If a custom vroom endpoint was specified update the configuration
        if (network?.endpoints?.vroom?.origin) {
          const { apiOrigin, apiVersion, domainSettings } = getApiConfiguration();
          const updatedApiOrigin = { ...apiOrigin };
          const updatedApiVersion = { ...apiVersion };
          updatedApiOrigin.vroomOrigin = network.endpoints.vroom.origin;

          // @HACK: The caller isn't supplying these so we will configure the API
          // as if it were calling ODB.
          Object.assign(updatedApiVersion, apiVersionCOB);

          // Extract the hostname from Vroom API endpoint.
          const apiURL = new URL(updatedApiOrigin.vroomOrigin);
          domainSettings[apiURL.hostname] = { scope: "Files.ReadWrite.All" };

          // Update the ApiConfiguration based on the updated origin and version values.
          setApiConfiguration(updatedApiOrigin, updatedApiVersion);
        }

        eventContext.addEventListener("scenarioComplete", scenarioComplete);
        eventContext.addEventListener("scenarioStart", scenarioStart);

        // Now that we have successfully processed the embed parameters we will
        // expose it as the embed context.
        embedContext = { ...embedOptions, hostClient };
      } catch (err) {
        // Ignore any mis-configured embed options.
        // @TODO: Send telemetry about misconfigured embed options.
      }
    }

    return { embedContext, scenarioComplete, scenarioStart };

    function scenarioComplete() {
      embedContext.hostClient?.sendNotification({ notification: "navigation-ended", timestamp: Date.now() });
    }

    function scenarioStart() {
      embedContext.hostClient?.sendNotification({ notification: "navigation-started", timestamp: Date.now() });
    }
  }, [additionalParameters, embed, eventContext]);

  // Dispose the host client when the application unmounts.
  React.useEffect(() => {
    return () => {
      embedContext?.hostClient?.dispose();
    };
  }, [embedContext]);

  React.useEffect(() => {
    return () => {
      eventContext.removeEventListener("scenarioComplete", scenarioComplete);
      eventContext.removeEventListener("scenarioStart", scenarioStart);
    };
  }, [eventContext, scenarioComplete, scenarioStart]);

  // If the host has defined a sessionId we will use it instead of our
  // locally generated version
  if (embedContext?.telemetry?.sessionId) {
    sessionContext.session = embedContext?.telemetry.sessionId;
  }

  // Setup some of the network requests we will use to initialize the application.
  // Api for retrieving basic account details.
  const _getAccountDetails = useFetch(getAccount, { blocking: false, name: "getAccountDetails" });
  const _getProfile = useFetch(getProfile, { blocking: false, name: "getProfile" });
  const _getProfileImage = useFetch(getProfileImage, { blocking: false, name: "getProfileImage" });
  const _getUserEmailPreferences = useFetch(getUserEmailPreferences, { blocking: false, name: "getUserEmailPreferences" });
  const _getUserPreferences = useFetch(getUserPreferences, { blocking: false, name: "getUserPreferences" });
  const _sendFeedback = useFetch(sendFeedback, { blocking: false, name: "sendFeedback", parentScenario: null, timeoutMs: 10000 });

  // Create the context we will use to send feedback to service.
  const sendFeedbackContext = new SendFeedbackContext(_sendFeedback, sessionContext, eventContext);

  // Update the state of the psedo-loc feature as it is changed.
  useSubscription(featureContext.featureEnabled("pseudoLoc"), (enabled) => {
    setCookie("PseudoLoc", enabled ? "pseudo" : "");
  });

  // Update the UxSetting with the latest changes to the selected theme.
  useSubscription(themeContext.currentTheme, (theme) => {
    settingsContext.mergeSetting<UXSetting>("uxSetting", { themeId: theme.themeId });
  });

  // Ensure we have an explicit direction (dir=ltr|rtl) attribute for our page to ensure
  // we get the appropriate styling (default to ltr).
  try {
    // Determine if the browser supports text direction detection based on languange.
    const locale = new Intl.Locale(sessionContext.locale);
    const textInfo: { direction?: "ltr" | "rtl" } | undefined = (locale as any).textInfo;

    if (textInfo && textInfo.direction) {
      document.documentElement.dir = textInfo.direction;
    }
  } catch {}

  if (!document.documentElement.dir) {
    document.documentElement.dir = "ltr";
  }

  // Create an error handler for dealing with failed network requests. All calls
  // using this handler should be background calls and wont present any UX on
  // failure.
  const handleRejection = useRejectionNoOp();

  // Handle creating an object url for the profile image.
  const { createObjectURL } = useObjectURL();

  // Get the app settings at Boot level
  const uxSetting = settingsContext.getSetting<UXSetting>("uxSetting");
  const appSettings = getAppSettingsForTelemetry(uxSetting, featureContext.getFeatureStates());

  // Get the navigation performance data and include it in the page load telemetry.
  const navigationEntries = window.performance.getEntriesByType("navigation");
  const navigation = navigationEntries && navigationEntries[0];

  React.useEffect(() => {
    // If the user is authenticated, we need to read the users profile and add
    // it to the current session context. If this fails we will just ignore it
    // and go on without a graph profile. This may affect features downstream.
    if (userAuthenticated) {
      // Setup the background from the persisted uxSetting.
      if (uxSetting?.backgroundImage) {
        backgroundContext.setBackground("shell", { photo: uxSetting.backgroundImage });
      }

      // Initialize the users details from the service.
      _getAccountDetails(sessionContext.driveId)
        .then(setAccountDetails)
        .catch((error) => eventContext.dispatchEvent("telemetryAvailable", { action: "exception", name: "getAccountDetails", error }));

      _getProfile()
        .then((profile) => {
          sessionContext.graphProfile.value = profile;
        })
        .catch(handleRejection);

      // Retrieve the profile image for the user, if it returns null, it means
      // the user doesnt have a profile image setup.
      _getProfileImage("120x120")
        .then((value) => {
          if (value) {
            sessionContext.profileUrl.value = createObjectURL(value);
          }
        })
        .catch(handleRejection);

      // For now we need to make a separate call for email preferences
      _getUserEmailPreferences()
        .then((value) => {
          userPreferencesContext.emailPreferences.value = value;
        })
        .catch(handleRejection);

      _getUserPreferences()
        .then((value) => {
          unstable_batchedUpdates(() => {
            if (value.photo) {
              settingsContext.setSetting("photoPreferences", value.photo);
              userPreferencesContext.photoPreferences.value = value.photo;
            }

            if (value.email) {
              settingsContext.setSetting("emailPreferences", value.email);
              userPreferencesContext.emailPreferences.value = value.email;
            }

            if (value.featureSettings) {
              settingsContext.setSetting("featurePreferences", value.featureSettings);
              userPreferencesContext.featurePreferences.value = value.featureSettings;

              // Store recent searches in observable array
              if (value.featureSettings.recentSearches) {
                // If we already have recentSearches it means people have done searches
                // before we loaded the recent. We will go ahead and cause an update
                // since we have new searches. The new searches will be added to the
                // start of the array.
                if (recentSearches.length) {
                  settingsLoaded.current = true;
                }

                recentSearches.splice(recentSearches.length, 0, ...(JSON.parse(value.featureSettings.recentSearches) as string[]));
                settingsLoaded.current = true;
              }
            }
          });
        })
        .catch(handleRejection);
    }

    // Cleanup network hooks each time we re-render.
    return () => {
      if (sessionContext.profileUrl.value) {
        window.URL.revokeObjectURL(sessionContext.profileUrl.value);
      }

      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleWorkerMessage);
      }

      window.removeEventListener("beforeunload", beforUnload);
      window.removeEventListener("error", unhandledException);

      eventContext.removeEventListener("telemetryAvailable", telemetryCallback);

      eventContext.removeEventListener("fetchPrepare", prepareRequest);
      eventContext.removeEventListener("fetchComplete", detectMigratedUser);
      eventContext.removeEventListener("fetchComplete", recordNetworkTelemetry);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [migrated]);

  // Determine if the request sent a login_hint for a user. If they are either
  // unauthenticated, or a hint for a different user, we will redirect through
  // AAD to pickup this login.
  const login_hint = searchParams.get("login_hint");
  if (login_hint) {
    const { userProfile } = sessionContext;
    if (!userAuthenticated || userProfile?.preferred_username !== login_hint) {
      window.location.href = getSignInUri(sessionContext, login_hint);

      // We are redirecting so we will just return nothing.
      return null;
    } else {
      const url = new URL(window.location.href);

      // If we had a login hint and didnt need to perform a login, we will strip
      // out the login_hint from the URL to prevent it from causing problems in
      // things like bookmarks later.
      url.searchParams.delete("login_hint");
      window.history.replaceState(null, "", url.toString());
    }
  }

  // @CONVERGENCE We use the users migrated state in the key for the Shell, this will
  // cause a new component instance to be created starting the entire load
  // process of the shell over again. This is important since the underlying
  // network calls will be routed to the new COB endpoints if we detect a
  // user has been migrated.
  return (
    <BrowserRouter>
      <EmbedContext.Provider value={embedContext}>
        <AuthorizationContext.Provider value={authorizationContext}>
          <WorkerContext.Provider value={{ workerDetails }}>
            <SettingsContext.Provider value={settingsContext}>
              <FeatureContext.Provider value={featureContext}>
                <ThemeContext.Provider value={themeContext}>
                  <FeedbackContext.Provider value={sendFeedbackContext}>
                    <SearchContext.Provider value={searchContext}>
                      <UserPreferencesContext.Provider value={userPreferencesContext}>
                        <MetaNavigation navigationParameters={additionalParameters}>
                          <Scenario
                            completeCallback={(_, result) => {
                              // If we are embedded and have a hostClient available we will send the page load message.
                              if (embedContext.hostClient) {
                                const notification = {
                                  error: result.status === "rejected" ? "Failed to load" : undefined,
                                  notification: "page-loaded",
                                  timestamp: Date.now()
                                };

                                embedContext.hostClient.sendNotification(notification);
                              }

                              setLoadComplete(true);
                            }}
                            scenarioName="photosAppLoad"
                            startTime={navigation?.startTime}
                            properties={{
                              entryPoint: searchParams.get("sc"),
                              height: window.innerHeight,
                              navigation,
                              pillar: "Boot",
                              width: window.innerWidth,
                              ...appSettings
                            }}
                          >
                            <Shell accountDetails={accountDetails} key={`migration-state-${migrated}`} loadComplete={loadComplete} />
                          </Scenario>
                        </MetaNavigation>
                      </UserPreferencesContext.Provider>
                    </SearchContext.Provider>
                  </FeedbackContext.Provider>
                </ThemeContext.Provider>
              </FeatureContext.Provider>
            </SettingsContext.Provider>
          </WorkerContext.Provider>
        </AuthorizationContext.Provider>
      </EmbedContext.Provider>
    </BrowserRouter>
  );

  function acquireHostToken(scope: string): Promise<string> {
    if (embedContext.hostClient) {
      const { apiOrigin } = getApiConfiguration();
      const authenticateCommand: IAuthenticateCommand = {
        command: "authenticate",
        resource: apiOrigin.vroomOrigin,
        type: "SharePoint"
      };

      return embedContext.hostClient.ready.then(() => {
        return embedContext
          .hostClient!.sendCommand<IAuthenticateCommand>(authenticateCommand)
          .then((response: any /* NOTE: IResult is improperly typed */) => {
            if (response.result === "token") {
              return response.token;
            } else {
              return Promise.reject(response.error.message);
            }
          });
      });
    }

    return Promise.resolve("");
  }

  /**
   * This event handler is executed before the page is unloaded.
   */
  function beforUnload(): void {
    handleSessionComplete("unload");
  }

  /**
   * detectMigratedUser is used in @CONVERGENCE to switch users from using ODC
   * to using the new COB endpoints.
   *
   * @param event The result of a network request.
   */
  function detectMigratedUser(event: IFetchCompleteEvent): void {
    const url = new URL(event.url);

    // For failures on onedrive consumer VROOM endpoint we want to detect a
    // migrated user and restart the experience, we will store the migrated
    // state in local storage once we detect a migrated user.
    if (event.result.status === "rejected" && event.result.reason && url.hostname === "api.onedrive.com") {
      const graphError = event.result.reason as Partial<IGraphError>;

      if (graphError.error && graphError.error.innererror && graphError.error.innererror.code === "userContentMigrated") {
        settingsContext.setSetting("userState", { migrated: true });
        setMigrated(true);
      }
    }
  }

  /**
   * handleSessionComplete is called when the current session is considered to be complete.
   * A session is defined to be complete when the user doesn't perform any userActions within the endTimeout.
   *
   * @param mode The place where the handleSessionComplete is called from
   */
  function handleSessionComplete(mode: string): void {
    // Do not send anything if it is an idle session
    if (startTime.current >= lastInteractTime.current) {
      return;
    }

    const PhotosWallTime = lastInteractTime.current - startTime.current;

    eventContext.dispatchEvent("telemetryAvailable", {
      action: "sessionComplete",
      mode,
      name: "sessionComplete",
      PhotosUserInteractions: numUserActions.current,
      PhotosWallTime
    });

    // Reset the start time to start a new session
    startTime.current = Date.now();
    numUserActions.current = 0;
  }

  function handleWorkerMessage(event: ExtendableMessageEvent): void {
    const data: IWorkerMessage = event.data;

    if (data) {
      if (data.type === "tokenRequest") {
        const resource = data.event.resource;
        let scope = data.event.scope;

        // If an origin is supplied we will translate that to a scope.
        if (resource) {
          const { domainSettings } = getApiConfiguration();
          const url = new URL(resource);

          scope = domainSettings[url.hostname]?.scope || scope;
        }

        authorizationContext
          .accessToken(scope)
          .then((accessToken) => {
            navigator.serviceWorker.controller?.postMessage({
              event: { accessToken },
              type: "tokenResponse"
            });
          })
          .catch(handleRejection);
      } else if (data.type === "workerTelemetry") {
        eventContext.dispatchEvent("telemetryAvailable", data.event);
      }
    }
  }

  /**
   * Given the origin of the request make sure we have the proper authentication
   * credentials attached to the request before it starts. This may involve
   * refreshing our credential.
   */
  function prepareRequest(event: IFetchPrepareEvent): void {
    const apiConfiguration = getApiConfiguration();

    // First prepare the origin and any potential apiVersion segments
    event.url = format(event.url, apiConfiguration.apiOrigin);
    event.url = format(event.url, apiConfiguration.apiVersion);

    // Force all api response to avoid intermediary caches. This can cause
    // problems with CORS since they may be returned without CORS headers.
    event.init.cache = "no-cache";

    if (sessionContext.authenticated()) {
      // Lookup the configuration for the target domain.
      const url = new URL(event.url);
      const configuration = apiConfiguration.domainSettings[url.hostname];

      // Make sure we keep any existing prefer directives along with our convergence values.
      // Don't add the migration directive to the search API it is still on ODC.
      if (url.hostname === "api.onedrive.com" && url.pathname.indexOf("/oneDrive.search(q=@s)") === -1) {
        const existingPrefer = url.searchParams.get("prefer");

        url.searchParams.set("prefer", `${existingPrefer ? `${existingPrefer};` : ""}Migration=EnableRedirect;FailOnMigratedFiles`);
        event.url = url.toString();

        // If we have a preferred user profile language we will set it as the
        // accept-language header for all requests.
        if (sessionContext.userProfile) {
          event.init.headers = Object.assign({ "Accept-Language": sessionContext.userProfile.locale }, event.init.headers);
        }
      }

      // Only add our Bearer token to the required domains, this will prevent
      // us from sending it to the wrong server.
      if (configuration && configuration.scope) {
        event.waitUntil(
          authorizationContext.accessToken(configuration.scope).then((accessToken) => {
            // If the request can be a "simple" CORS request we will include
            // the access_token in the URL. This will prevent the extra
            // pre-flight request that is not needed.
            if (configuration.supportsAccessParam) {
              // Make sure we SET the parameter the URL might have an outdated
              // token that was in the generated URL.
              url.searchParams.set("access_token", accessToken);
              event.url = url.toString();
            } else {
              event.init.headers = Object.assign({ Authorization: `Bearer ${accessToken}` }, event.init.headers);
            }
          })
        );
      }
    }
  }

  /**
   * Convert the completed network request into a telemetry event that can be send
   * to our external telemetry system. Ensure the event doesn't contain sensitive
   * data we don't want recorded.
   */

  function recordNetworkTelemetry(event: IFetchCompleteEvent): void {
    const { eventDispatch, fetch, name, ...options } = event.options || {};

    eventContext.dispatchEvent("telemetryAvailable", { action: "fetchComplete", ...event, options });
  }

  /**
   * When an unhandled exception is caught we want to send the details through
   * to the telemetry system.
   *
   * @param event Details about the error, this will be a script error.
   */
  function unhandledException(event: ErrorEvent): void {
    const stack = event.error?.stack.toString();

    // Don't report well-known errors that are internal to the browser
    // ResizeObserver loop limit exceeded - https://bugs.chromium.org/p/chromium/issues/detail?id=809574
    if (event.message === "ResizeObserver loop limit exceeded") {
      return;
    }

    try {
      eventContext.dispatchEvent("telemetryAvailable", {
        action: "exception",
        colno: event.colno,
        error: event.message,
        filename: event.filename,
        lineno: event.lineno,
        name: "unhandledException",
        stack
      });
    } catch (err) {
      // We failed to report the exception just eat it and move on.
    }
  }

  function updateSessionMetrics(event: ITelemetryEvent): void {
    // Count photosAppLoad scenario as the first entry user action
    if ((event.action === "scenarioComplete" && event.scenarioName === "photosAppLoad") || event.action === "userAction") {
      // Update the lastInteractTime and increment the number of user actions
      lastInteractTime.current = Date.now();
      numUserActions.current++;

      // If there is no telemetry sending out in 15 mins which means there is no activity,
      // then consider the session ends and call the handler function
      setTimeout(() => {
        handleSessionComplete("timeout");
      }, endTimeout);
    }
  }
}

/**
 * use to prepare an object that contains all app level settings to be used for telemetry
 *
 * @param uxSetting The UX tracks all common settings in the "uxSetting" value
 * @param featureSetting The faeture availble to the user
 *
 * @returns An object of settings
 */
export function getAppSettingsForTelemetry(
  uxSetting: UXSetting | undefined,
  featureSetting: { [setting: string]: boolean } | undefined
): { [key: string]: any } {
  let appSettings = {} as { [key: string]: any };
  if (uxSetting) {
    appSettings = Object.keys(uxSetting).reduce((settings, key) => {
      settings["ux_" + key] = uxSetting[key as keyof UXSetting];
      return settings;
    }, appSettings);
  }
  if (featureSetting) {
    appSettings = Object.keys(featureSetting).reduce((settings, key) => {
      settings["feature_" + key] = featureSetting[key];
      return settings;
    }, appSettings);
  }

  return appSettings;
}

export default Application;
